import { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select, Alert, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup, Collapse, Button, Skeleton } from "@mui/material";
import { Label, Toggle } from "@fluentui/react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import { getHistoryForForecast, ForecastSourceError } from "../../api";
import { create4DRegistrationObject, createPeoplenetRegistrationObject, ICourseRegistration } from "../../utilities/utils";
import {
    aggregateRevenueByYearMonth,
    aggregateCountByYearMonth,
    clipToLastCompleteMonth,
    YearMonthPoint,
} from "../../utilities/dashboardAggregations";
import { runForecast, ForecastResult, summarizeForecastForYear, ConfidenceBucket } from "../../utilities/forecast";
import { ForecastChart } from "./ForecastChart";
import { ForecastKpiCards } from "./ForecastKpiCards";

// ------------------------------------------------------------------
// ForecastView — "Prognose" tab.
//
// Layout:
//   1. Hero: 3 KPI cards (Omsætning 12m, Tilmeldinger 12m, Sikkerhed)
//   2. Metric pills: [Omsætning] [Tilmeldinger]
//   3. Main chart (total, with firm filter applied)
//   4. Per-category breakdown (Omsætning only) or Per-firma (both)
//   5. Compact controls row: firm dropdown
//   6. Collapsed "Avanceret": ekskluder interne + vis beregningsdetaljer
//
// Auto-loads on tab mount. Only shows a retry button if the fetch fails.
// ------------------------------------------------------------------

const HISTORY_FLOOR = new Date(2023, 0, 1);
const INTERNAL_EMAIL_DOMAINS = ["@peoplenet.dk", "@4d.dk"];

type FirmFilter = "Alle" | "4D" | "PeopleNet";
type MetricTab = "revenue" | "registrations";

function isInternalRegistration(reg: ICourseRegistration): boolean {
    const email = reg.contactPersonEmail?.trim().toLowerCase();
    if (!email) return false;
    return INTERNAL_EMAIL_DOMAINS.some(d => email.endsWith(d));
}

function periodString(d: Date): string {
    return d.toISOString();
}

function worstConfidence(a: ConfidenceBucket | null, b: ConfidenceBucket | null): ConfidenceBucket {
    const rank: Record<ConfidenceBucket, number> = { "Høj": 3, "Middel": 2, "Lav": 1 };
    const av = a ? rank[a] : 3;
    const bv = b ? rank[b] : 3;
    const w = Math.min(av, bv);
    return w === 3 ? "Høj" : w === 2 ? "Middel" : "Lav";
}

export function ForecastView() {
    const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [firm, setFirm] = useState<FirmFilter>("Alle");
    const [includeInternal, setIncludeInternal] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState<MetricTab>("revenue");
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showMethods, setShowMethods] = useState(false);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState<ICourseRegistration[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Acquire token on mount.
    useEffect(() => {
        if (!accessToken) {
            instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] })
                .then(r => r?.accessToken && setAccessToken(r.accessToken));
        }
    }, []);

    // Auto-load history as soon as we have a token. No manual click required.
    useEffect(() => {
        if (accessToken && !history && !loading && !error) {
            loadHistory();
        }
    }, [accessToken]);

    const loadHistory = async () => {
        if (!accessToken) return;
        setLoading(true);
        setError(null);
        try {
            const start = new Date(HISTORY_FLOOR); start.setHours(0, 0, 0, 0);
            const end = new Date(); end.setHours(23, 59, 59, 999);
            const { fourD, peopleNet } = await getHistoryForForecast(
                accessToken,
                "Alle",
                { startDate: periodString(start), endDate: periodString(end) }
            );
            const merged = [
                ...create4DRegistrationObject(fourD),
                ...createPeoplenetRegistrationObject(peopleNet),
            ];
            setHistory(merged);
        } catch (e) {
            if (e instanceof ForecastSourceError) {
                const cause: any = e.cause;
                const status = cause?.response?.status;
                const statusText = cause?.response?.statusText;
                const serverMsg =
                    (typeof cause?.response?.data === "string" && cause.response.data) ||
                    cause?.response?.data?.message ||
                    cause?.message ||
                    "";
                // eslint-disable-next-line no-console
                console.error("Forecast history fetch failed:", e.source, cause);
                const source = e.source === "4D" ? "4D" : "PeopleNet";
                const detail = status ? ` (HTTP ${status}${statusText ? " " + statusText : ""}${serverMsg ? ": " + serverMsg : ""})` : (serverMsg ? `: ${serverMsg}` : "");
                setError(`Vi kan ikke hente data fra ${source} lige nu${detail}.`);
            } else {
                // eslint-disable-next-line no-console
                console.error("Forecast history fetch failed (unknown):", e);
                setError("Der skete en ukendt fejl. Prøv igen om lidt.");
            }
            setHistory(null);
        } finally {
            setLoading(false);
        }
    };

    // Client-side filter over the cached raw history.
    const filtered = useMemo(() => {
        if (!history) return null;
        let rows = history;
        if (firm !== "Alle") {
            rows = rows.filter(r => r.sourceOrg === firm);
        }
        if (!includeInternal) {
            rows = rows.filter(r => !isInternalRegistration(r));
        }
        return rows;
    }, [history, firm, includeInternal]);

    // Compute all forecasts once per filter change. Memoised.
    const forecasts = useMemo(() => {
        if (!filtered) return null;

        const mkTotal = (series: YearMonthPoint[]): ForecastResult =>
            runForecast(clipToLastCompleteMonth(series));

        return {
            revenueTotal: mkTotal(aggregateRevenueByYearMonth(filtered, firm)),
            registrationsTotal: mkTotal(aggregateCountByYearMonth(filtered)),
        };
    }, [filtered, firm]);

    // Summaries for the 3 hero KPI cards — current calendar year.
    const currentYear = new Date().getFullYear();
    const revenueSummary = forecasts ? summarizeForecastForYear(forecasts.revenueTotal, currentYear) : null;
    const registrationsSummary = forecasts ? summarizeForecastForYear(forecasts.registrationsTotal, currentYear) : null;
    const revConfidence = revenueSummary?.confidence ?? null;
    const regConfidence = registrationsSummary?.confidence ?? null;
    const heroConfidence = worstConfidence(revConfidence, regConfidence);
    const worstBandKr = revenueSummary?.bandHalfWidth ?? 0;

    const hasAnyForecast = forecasts !== null && (
        forecasts.revenueTotal.ensemble.length > 0 ||
        forecasts.registrationsTotal.ensemble.length > 0
    );

    return (
        <Box sx={{ width: "100%" }}>
            <h2 style={{ marginTop: 0, marginBottom: 4, color: "#1a1a1a" }}>Prognose</h2>
            <p style={{ color: "#5f6368", marginTop: 0, marginBottom: 24 }}>
                Sådan forventer vi det næste år bliver, baseret på historikken fra 2023 og frem.
            </p>

            {/* Error state */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={loadHistory}
                            disabled={loading}
                        >
                            Prøv igen
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {/* Loading skeleton for hero cards */}
            {loading && !history && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 28 }}>
                    <Skeleton variant="rounded" height={140} />
                    <Skeleton variant="rounded" height={140} />
                    <Skeleton variant="rounded" height={140} />
                </div>
            )}

            {/* Hero KPI cards */}
            {forecasts && hasAnyForecast && (
                <ForecastKpiCards
                    revenue={revenueSummary}
                    registrations={registrationsSummary}
                    confidence={heroConfidence}
                    worstBandKr={worstBandKr}
                />
            )}

            {/* Metric tabs */}
            {forecasts && hasAnyForecast && (
                <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <ToggleButtonGroup
                        value={selectedMetric}
                        exclusive
                        onChange={(_, v) => {
                            if (v === "revenue" || v === "registrations") setSelectedMetric(v);
                        }}
                        size="small"
                    >
                        <ToggleButton value="revenue" sx={{ textTransform: "none", px: 2.5 }}>Omsætning</ToggleButton>
                        <ToggleButton value="registrations" sx={{ textTransform: "none", px: 2.5 }}>Tilmeldinger</ToggleButton>
                    </ToggleButtonGroup>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "auto" }}>
                        <Label style={{ fontSize: 12, color: "#5f6368" }}>Firma</Label>
                        <Select
                            size="small"
                            value={firm}
                            onChange={(e) => {
                                const v: string = String(e.target.value);
                                if (v === "Alle" || v === "4D" || v === "PeopleNet") setFirm(v);
                            }}
                            sx={{ minWidth: 140 }}
                        >
                            <MenuItem value="Alle">Vis alle</MenuItem>
                            <MenuItem value="4D">4D</MenuItem>
                            <MenuItem value="PeopleNet">PeopleNet</MenuItem>
                        </Select>
                    </Box>
                </Box>
            )}

            {/* Main content */}
            {forecasts && hasAnyForecast && selectedMetric === "revenue" && (
                <ForecastChart
                    title="Omsætning pr. måned"
                    unit="kr"
                    forecast={forecasts.revenueTotal}
                    showMethods={showMethods}
                />
            )}

            {forecasts && hasAnyForecast && selectedMetric === "registrations" && (
                <ForecastChart
                    title="Tilmeldinger pr. måned"
                    unit="stk"
                    forecast={forecasts.registrationsTotal}
                    showMethods={showMethods}
                />
            )}

            {/* Advanced (collapsed by default) */}
            {forecasts && hasAnyForecast && (
                <Box sx={{ mt: 3, borderTop: "1px solid #eee", pt: 1 }}>
                    <Button
                        size="small"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ textTransform: "none", color: "#5f6368" }}
                    >
                        Avanceret
                    </Button>
                    <Collapse in={showAdvanced}>
                        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1, mb: 2 }}>
                            <Toggle
                                label="Ekskluder interne tilmeldinger"
                                checked={!includeInternal}
                                onText="Ja"
                                offText="Nej"
                                onChange={(_, checked) => setIncludeInternal(!checked)}
                            />
                            <FormControlLabel
                                control={<Switch checked={showMethods} onChange={(_, c) => setShowMethods(c)} />}
                                label="Vis beregningsdetaljer"
                            />
                        </Box>
                    </Collapse>
                </Box>
            )}

            {/* Empty state */}
            {!loading && !error && forecasts && !hasAnyForecast && (
                <p style={{ color: "#666" }}>
                    Ingen data matcher det valgte filter. Prøv at fjerne filtre.
                </p>
            )}
        </Box>
    );
}
